import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  List,
  Search,
  AlertCircle,
  Bug,
  CheckCircle,
  Clock,
  Filter,
  ChevronDown,
  ChevronUp,
  User,
  Settings,
  Flag,
  Mail
} from 'lucide-react';

interface ListResultsMessageProps {
  content: string;
  structured_data: any;
  onActionClick?: (action: string, itemId: string, itemData?: any) => void;
}

/**
 * Component for rendering list results (Linear issues, customers, etc.)
 */
export const ListResultsMessage: React.FC<ListResultsMessageProps> = ({ 
  content, 
  structured_data,
  onActionClick 
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState<string>('all');
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownSelections, setDropdownSelections] = useState<Record<string, string>>({});
  
  const data = structured_data?.data;
  const items = data?.items || [];
  const totalCount = data?.total_count || 0;
  const displayedCount = data?.displayed_count || 0;
  const entityType = data?.entity_type || 'items';
  const title = data?.title || content;

  // Get appropriate icon for entity type
  const getEntityIcon = () => {
    switch (entityType) {
      case 'issues':
        return <Bug className="h-4 w-4 text-red-500" />;
      case 'linear_issues':
        return <div className="flex items-center gap-1">
          <Bug className="h-4 w-4 text-violet-500" />
          <span className="text-xs font-medium text-violet-600">Linear</span>
        </div>;
      case 'linear_teams':
        return <div className="flex items-center gap-1">
          <User className="h-4 w-4 text-violet-500" />
          <span className="text-xs font-medium text-violet-600">Linear</span>
        </div>;
      case 'customers':
      case 'contacts':
        return <User className="h-4 w-4 text-green-500" />;
      case 'stripe_customers':
        return <div className="flex items-center gap-1">
          <User className="h-4 w-4 text-indigo-500" />
          <span className="text-xs font-medium text-indigo-600">Stripe</span>
        </div>;
      case 'tickets':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'teams':
        return <User className="h-4 w-4 text-purple-500" />;
      default:
        return <List className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get state-specific styling
  const getStateBadgeColor = (state: string) => {
    switch (state.toLowerCase()) {
      case 'triage':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'done':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'backlog':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Filter items based on search and state
  const filteredItems = items.filter((item: any) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      item.title?.toLowerCase().includes(searchLower) ||
      item.subtitle?.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.email?.toLowerCase().includes(searchLower) ||
      // Enhanced search for customers/contacts
      (entityType === 'customers' || entityType === 'contacts') && (
        item.company_name?.toLowerCase().includes(searchLower) ||
        item.firstname?.toLowerCase().includes(searchLower) ||
        item.lastname?.toLowerCase().includes(searchLower) ||
        item.department?.toLowerCase().includes(searchLower) ||
        item.source?.toLowerCase().includes(searchLower) ||
        item.company_website?.toLowerCase().includes(searchLower)
      ) ||
      // Enhanced search for Stripe customers
      entityType === 'stripe_customers' && (
        item.name?.toLowerCase().includes(searchLower) ||
        item.stripe_id?.toLowerCase().includes(searchLower) ||
        item.email?.toLowerCase().includes(searchLower)
      ) ||
      // Enhanced search for Linear issues
      entityType === 'linear_issues' && (
        item.title?.toLowerCase().includes(searchLower) ||
        item.state?.toLowerCase().includes(searchLower) ||
        item.issueType?.toLowerCase().includes(searchLower) ||
        item.metadata?.category?.toLowerCase().includes(searchLower) ||
        item.metadata?.errorType?.toLowerCase().includes(searchLower) ||
        item.linear_id?.toLowerCase().includes(searchLower)
      ) ||
      // Enhanced search for Linear teams
      entityType === 'linear_teams' && (
        item.name?.toLowerCase().includes(searchLower) ||
        item.teamType?.toLowerCase().includes(searchLower) ||
        item.metadata?.teamType?.toLowerCase().includes(searchLower) ||
        item.members?.some((member: any) =>
          member.name?.toLowerCase().includes(searchLower) ||
          member.email?.toLowerCase().includes(searchLower)
        ) ||
        item.metadata?.memberDomains?.some((domain: string) =>
          domain.toLowerCase().includes(searchLower)
        ) ||
        item.linear_team_id?.toLowerCase().includes(searchLower)
      ) ||
      // Enhanced search for teams
      entityType === 'teams' && (
        item.name?.toLowerCase().includes(searchLower) ||
        item.member_names?.toString().toLowerCase().includes(searchLower) ||
        item.member_emails?.toString().toLowerCase().includes(searchLower)
      );
    
    const matchesState = filterState === 'all' || 
      item.state?.toLowerCase() === filterState.toLowerCase();
    
    return matchesSearch && matchesState;
  });

  // Get unique states for filter
  const uniqueStates = [...new Set(items.map((item: any) => item.state))].filter(Boolean);

  // Limit displayed items when not expanded
  const displayItems = isExpanded ? filteredItems : filteredItems.slice(0, 10);
  
  // Handle action clicks
  const handleActionClick = (action: string, itemId: string, itemData?: any) => {
    console.log('🔗 [ListResultsMessage] Action clicked:', { action, itemId, itemData });
    if (onActionClick) {
      onActionClick(action, itemId, itemData);
    }
  };
  
  // Handle dropdown selection changes
  const handleDropdownChange = (itemId: string, dropdownType: string, value: string, action: string) => {
    console.log('📋 [ListResultsMessage] Dropdown changed:', { itemId, dropdownType, value, action });
    
    // Update local state
    setDropdownSelections(prev => ({
      ...prev,
      [`${itemId}-${dropdownType}`]: value
    }));
    
    // Trigger action
    if (onActionClick) {
      onActionClick(action, itemId, { [dropdownType]: value, value });
    }
  };
  
  // Render dropdown controls
  const renderDropdownControls = (item: any) => {
    const controls = structured_data?.controls;
    if (!controls) return null;
    
    return (
      <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
        {/* Assignee Dropdown */}
        {controls.assignee_dropdown && (
          <div className="flex items-center gap-2">
            <User className="h-3 w-3 text-gray-500" />
            <Select
              value={dropdownSelections[`${item.id}-assignee`] || ''}
              onValueChange={(value) => handleDropdownChange(item.id, 'assignee', value, controls.assignee_dropdown.action)}
            >
              <SelectTrigger className="w-32 h-7 text-xs">
                <SelectValue placeholder={controls.assignee_dropdown.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {controls.assignee_dropdown.options?.map((option: any) => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span>{option.label}</span>
                      {option.email && (
                        <span className="text-gray-500 text-xs">({option.email})</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Status Dropdown */}
        {controls.status_dropdown && (
          <div className="flex items-center gap-2">
            <Settings className="h-3 w-3 text-gray-500" />
            <Select
              value={dropdownSelections[`${item.id}-status`] || ''}
              onValueChange={(value) => handleDropdownChange(item.id, 'status', value, controls.status_dropdown.action)}
            >
              <SelectTrigger className="w-28 h-7 text-xs">
                <SelectValue placeholder={controls.status_dropdown.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {controls.status_dropdown.options?.map((option: any) => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    <div className="flex items-center gap-2">
                      {option.color && (
                        <div 
                          className="w-3 h-3 rounded-full border"
                          style={{ 
                            backgroundColor: `${option.color}30`,
                            borderColor: option.color 
                          }}
                        />
                      )}
                      <span>{option.emoji} {option.label || option.name || option.value}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {/* Priority Dropdown */}
        {controls.priority_dropdown && (
          <div className="flex items-center gap-2">
            <Flag className="h-3 w-3 text-gray-500" />
            <Select
              value={dropdownSelections[`${item.id}-priority`] || ''}
              onValueChange={(value) => handleDropdownChange(item.id, 'priority', value, controls.priority_dropdown.action)}
            >
              <SelectTrigger className="w-24 h-7 text-xs">
                <SelectValue placeholder={controls.priority_dropdown.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {controls.priority_dropdown.options?.map((option: any) => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    <div className="flex items-center gap-2">
                      {option.color && (
                        <div 
                          className="w-3 h-3 rounded-full border"
                          style={{ 
                            backgroundColor: `${option.color}30`,
                            borderColor: option.color 
                          }}
                        />
                      )}
                      <span>{option.emoji} {option.label || option.name || option.value}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="w-full bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {getEntityIcon()}
          {title}
          <Badge variant="secondary" className="ml-auto">
            {displayedCount} of {totalCount}
          </Badge>
        </CardTitle>
        
        {/* Search and Filter Bar */}
        <div className="flex gap-2 mt-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={
                entityType === 'customers' || entityType === 'contacts'
                  ? `Search by name, email, company, department...`
                  : entityType === 'stripe_customers'
                  ? `Search by name, email, or Stripe ID...`
                  : entityType === 'linear_issues'
                  ? `Search by title, state, issue type, or error...`
                  : entityType === 'linear_teams'
                  ? `Search by team name, members, or domain...`
                  : entityType === 'teams'
                  ? `Search by team name, members...`
                  : `Search ${entityType}...`
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
          
          {uniqueStates.length > 0 && (
            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-200 rounded-md text-sm"
            >
              <option value="all">All States</option>
              {uniqueStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-3">
          {displayItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No {entityType} found matching your criteria</p>
            </div>
          ) : (
            displayItems.map((item: any, index: number) => {
              const isClickable = item.url || item.metadata?.url;
              const handleItemClick = () => {
                const url = item.url || item.metadata?.url;
                if (url) {
                  navigate(url);
                }
              };

              return (
                <div
                  key={item.id || index}
                  className={`p-4 bg-white rounded-xl border border-gray-100 transition-all duration-200 ${
                    isClickable 
                      ? 'hover:border-blue-200 hover:shadow-md cursor-pointer hover:bg-blue-50/30' 
                      : 'hover:border-gray-200 hover:shadow-sm'
                  }`}
                  onClick={isClickable ? handleItemClick : undefined}
                >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-gray-900 text-base leading-tight truncate">
                            {item.title || 'Untitled'}
                          </h4>
                          <div className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                            <Clock className="h-3 w-3" />
                            {item.timestamp || new Date(item.createdAt || Date.now()).toLocaleDateString()}
                          </div>
                        </div>
                        
                        {item.subtitle && (
                          <p className="text-sm text-gray-600 mb-2 truncate">{item.subtitle}</p>
                        )}
                        
                        {item.description && item.description !== 'No description' && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {item.description}
                          </p>
                        )}
                        
                        {/* Status and Priority Badges Row */}
                        <div className="flex items-center gap-2 mb-3">
                          {/* Status Badge with Icon */}
                          {item.metadata?.status && (
                            <div 
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${item.metadata.status_color}15`,
                                color: item.metadata.status_color,
                                border: `1px solid ${item.metadata.status_color}30`
                              }}
                            >
                              <div 
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: item.metadata.status_color }}
                              />
                              {item.metadata.status}
                            </div>
                          )}
                          
                          {/* Priority Badge with Icon */}
                          {item.metadata?.priority && (
                            <div 
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${item.metadata.priority_color}15`,
                                color: item.metadata.priority_color,
                                border: `1px solid ${item.metadata.priority_color}30`
                              }}
                            >
                              {item.metadata.priority === 'High' && <Flag className="h-3 w-3" />}
                              {item.metadata.priority === 'Medium' && <Flag className="h-3 w-3" />}
                              {item.metadata.priority === 'Low' && <Flag className="h-3 w-3 opacity-60" />}
                              {item.metadata.priority}
                            </div>
                          )}
                          
                        </div>

                        {/* Context Row - Different for different entity types */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {/* For Tickets */}
                          {entityType === 'tickets' && (
                            <>
                              {/* Assignee */}
                              {item.metadata.assignedToName && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-lg text-xs font-medium text-blue-700 max-w-32">
                                  <User className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{item.metadata.assignedToName}</span>
                                </div>
                              )}
                              
                              {/* Customer */}
                              {(item.metadata.customerName || item.metadata.customerEmail) && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 rounded-lg text-xs font-medium text-purple-700 max-w-40">
                                  <div className="w-2 h-2 bg-purple-400 rounded-full flex-shrink-0" />
                                  <span className="truncate">{item.metadata.customerName || item.metadata.customerEmail}</span>
                                </div>
                              )}
                              
                              {/* Teams */}
                              {item.metadata.teamNames && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg text-xs font-medium text-green-700 max-w-36">
                                  <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" />
                                  <span className="truncate">
                                    {Array.isArray(item.metadata.teamNames) 
                                      ? item.metadata.teamNames.map((t: any) => 
                                          typeof t === 'string' && t.includes('[') 
                                            ? t.replace(/[\[\]']/g, '') 
                                            : t
                                        ).join(', ')
                                      : item.metadata.teamNames}
                                  </span>
                                </div>
                              )}
                            </>
                          )}

                          {/* For Linear Teams */}
                          {entityType === 'linear_teams' && (
                            <>
                              {/* Linear Team Header */}
                              <div className="w-full mb-3">
                                <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-200">
                                  <div className="flex items-center gap-3">
                                    <div className={`
                                      w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center flex-shrink-0
                                      ${item.metadata?.teamType === 'Product'
                                        ? 'from-blue-500 to-purple-600'
                                        : item.metadata?.teamType === 'Engineering'
                                        ? 'from-violet-500 to-indigo-600'
                                        : item.metadata?.teamType === 'Business'
                                        ? 'from-green-500 to-teal-600'
                                        : 'from-gray-500 to-slate-600'
                                      }
                                    `}>
                                      <span className="text-white text-sm font-bold">
                                        {item.metadata?.teamIcon || '👥'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-base font-semibold text-gray-900 truncate">
                                        {item.name}
                                      </div>
                                      <div className="text-xs text-violet-600 truncate font-mono">
                                        🆔 {item.linear_team_id}
                                      </div>
                                      {item.metadata?.key && (
                                        <div className="text-xs text-gray-600 truncate mt-1">
                                          🔑 {item.metadata.key}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-shrink-0">
                                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-100 rounded-lg text-xs font-medium text-violet-700">
                                        <span>⚪</span>
                                        <span>Linear</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Team Details */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                {/* Team Type Badge */}
                                <div className={`
                                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                                  ${item.metadata?.teamType === 'Product'
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                    : item.metadata?.teamType === 'Engineering'
                                    ? 'bg-violet-50 text-violet-700 border border-violet-200'
                                    : item.metadata?.teamType === 'Business'
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-gray-50 text-gray-700 border border-gray-200'
                                  }
                                `}>
                                  {item.metadata?.teamIcon || '👥'}
                                  <span>{item.metadata?.teamType || 'General'}</span>
                                </div>

                                {/* Member Count Badge */}
                                <div className={`
                                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                                  ${item.metadata?.memberCountValid
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                  }
                                `}>
                                  👥
                                  <span>
                                    {item.member_count} member{item.member_count !== 1 ? 's' : ''}
                                    {!item.metadata?.memberCountValid && ` (${item.metadata?.actualMemberCount} actual)`}
                                  </span>
                                </div>

                                {/* Domain Badges */}
                                {item.metadata?.memberDomains?.map((domain: string) => (
                                  <div key={domain} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg text-xs font-medium text-gray-700 border border-gray-200">
                                    🌐 <span className="truncate">@{domain}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Team Members */}
                              {item.members && item.members.length > 0 && (
                                <div className="bg-white p-3 rounded-lg border border-gray-200 mb-2">
                                  <h6 className="text-sm font-medium text-gray-900 mb-2">Team Members</h6>
                                  <div className="space-y-2">
                                    {item.members.slice(0, 4).map((member: any, index: number) => (
                                      <div key={member.id || index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                        <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                                          <span className="text-white text-xs font-bold">
                                            {member.name ? member.name.split('@')[0][0].toUpperCase() : '👤'}
                                          </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900 truncate">
                                            {member.name || member.email}
                                          </div>
                                          {member.email && member.email !== member.name && (
                                            <div className="text-xs text-gray-500 truncate">
                                              {member.email}
                                            </div>
                                          )}
                                        </div>
                                        {member.email && (
                                          <a
                                            href={`mailto:${member.email}`}
                                            className="p-1 text-gray-400 hover:text-violet-600 transition-colors"
                                          >
                                            <Mail className="h-3 w-3" />
                                          </a>
                                        )}
                                      </div>
                                    ))}
                                    {item.members.length > 4 && (
                                      <div className="text-xs text-gray-500 text-center py-1">
                                        +{item.members.length - 4} more member{item.members.length - 4 !== 1 ? 's' : ''}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Team Description */}
                              {item.description && (
                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-2">
                                  <p className="text-sm text-gray-700">{item.description}</p>
                                </div>
                              )}
                            </>
                          )}

                          {/* For Linear Issues */}
                          {entityType === 'linear_issues' && (
                            <>
                              {/* Linear Issue Header */}
                              <div className="w-full mb-3">
                                <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-3 border border-violet-200">
                                  <div className="flex items-center gap-3">
                                    <div className={`
                                      w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center flex-shrink-0
                                      ${item.metadata?.issueType === 'Bug'
                                        ? 'from-red-500 to-pink-600'
                                        : item.metadata?.issueType === 'Task'
                                        ? 'from-blue-500 to-indigo-600'
                                        : 'from-violet-500 to-purple-600'
                                      }
                                    `}>
                                      <span className="text-white text-sm font-bold">
                                        {item.metadata?.issueType === 'Bug' ? '🐛' :
                                         item.metadata?.issueType === 'Task' ? '📋' : '⚡'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-base font-semibold text-gray-900 truncate">
                                        {item.title}
                                      </div>
                                      <div className="text-xs text-violet-600 truncate font-mono">
                                        🆔 {item.linear_id}
                                      </div>
                                      {item.metadata?.category && (
                                        <div className="text-xs text-gray-600 truncate mt-1">
                                          📂 {item.metadata.category}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-shrink-0">
                                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-violet-100 rounded-lg text-xs font-medium text-violet-700">
                                        <span>⚪</span>
                                        <span>Linear</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Issue Details */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                {/* State Badge */}
                                <div
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
                                  style={{
                                    backgroundColor: `${item.metadata?.state_color}15`,
                                    color: item.metadata?.state_color,
                                    border: `1px solid ${item.metadata?.state_color}30`
                                  }}
                                >
                                  <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: item.metadata?.state_color }}
                                  />
                                  {item.state}
                                </div>

                                {/* Priority Badge */}
                                {item.priority > 0 && (
                                  <div
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium"
                                    style={{
                                      backgroundColor: `${item.metadata?.priority_color}15`,
                                      color: item.metadata?.priority_color,
                                      border: `1px solid ${item.metadata?.priority_color}30`
                                    }}
                                  >
                                    <Flag className="h-3 w-3" />
                                    {item.metadata?.priorityLabel} Priority
                                  </div>
                                )}

                                {/* Issue Type Badge */}
                                <div className={`
                                  inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium
                                  ${item.metadata?.issueType === 'Bug'
                                    ? 'bg-red-50 text-red-700 border border-red-200'
                                    : item.metadata?.issueType === 'Task'
                                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                    : 'bg-violet-50 text-violet-700 border border-violet-200'
                                  }
                                `}>
                                  {item.metadata?.issueType === 'Bug' ? '🐛' :
                                   item.metadata?.issueType === 'Task' ? '📋' : '⚡'}
                                  <span>{item.metadata?.issueType}</span>
                                </div>

                                {/* Error Type Badge */}
                                {item.metadata?.errorType && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 rounded-lg text-xs font-medium text-orange-700 border border-orange-200">
                                    ⚠️ <span className="truncate">{item.metadata.errorType}</span>
                                  </div>
                                )}
                              </div>

                              {/* Issue Description */}
                              {item.description && (
                                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-2">
                                  <p className="text-sm text-gray-700">{item.description}</p>
                                </div>
                              )}
                            </>
                          )}

                          {/* For Stripe Customers */}
                          {entityType === 'stripe_customers' && (
                            <>
                              {/* Stripe Customer Information */}
                              <div className="w-full mb-3">
                                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-200">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                      <span className="text-white text-sm font-bold">
                                        {item.name ? item.name[0].toUpperCase() : '💳'}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-base font-semibold text-gray-900 truncate">
                                        {item.name || item.email || `Customer ${item.stripe_id}`}
                                      </div>
                                      <div className="text-xs text-indigo-600 truncate font-mono">
                                        🆔 {item.stripe_id || item.id}
                                      </div>
                                      {item.created_timestamp && (
                                        <div className="text-xs text-gray-600 truncate mt-1">
                                          📅 Created {new Date(item.created_timestamp * 1000).toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-shrink-0">
                                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-100 rounded-lg text-xs font-medium text-indigo-700">
                                        <span>💳</span>
                                        <span>Stripe</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Contact Details */}
                              <div className="flex flex-wrap gap-2">
                                {/* Email */}
                                {item.email && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-lg text-xs font-medium text-blue-700 max-w-48">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                                    <span className="truncate">{item.email}</span>
                                  </div>
                                )}

                                {/* Customer Status */}
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg text-xs font-medium text-green-700 max-w-32">
                                  ✅ <span className="truncate">Active</span>
                                </div>

                                {/* Creation Date */}
                                {item.created_timestamp && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-lg text-xs font-medium text-yellow-700 max-w-32">
                                    🕐 <span className="truncate">{new Date(item.created_timestamp * 1000).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* For Customers/Contacts */}
                          {(entityType === 'customers' || entityType === 'contacts') && (
                            <>
                              {/* Company Information - Prioritized */}
                              {(item.company_name || item.company || item.companyId) && (
                                <div className="w-full mb-3">
                                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-3 border border-indigo-200">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <span className="text-white text-sm font-bold">
                                          {(item.company_name || item.company) ? (item.company_name || item.company)[0].toUpperCase() : '🏢'}
                                        </span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-base font-semibold text-gray-900 truncate">
                                          {item.company_name || item.company || 'Company'}
                                        </div>
                                        {item.company_website && (
                                          <div className="text-xs text-indigo-600 truncate">
                                            🌐 {item.company_website}
                                          </div>
                                        )}
                                        {item.company_description && (
                                          <div className="text-xs text-gray-600 truncate mt-1">
                                            {item.company_description}
                                          </div>
                                        )}
                                        {item.companyId && !(item.company_name || item.company) && (
                                          <div className="text-xs text-gray-500 font-mono truncate">
                                            ID: {item.companyId.substring(0, 8)}...
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Contact Details */}
                              <div className="flex flex-wrap gap-2">
                                {/* Email */}
                                {item.email && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 rounded-lg text-xs font-medium text-blue-700 max-w-48">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                                    <span className="truncate">{item.email}</span>
                                  </div>
                                )}
                                
                                {/* Phone */}
                                {item.phone && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg text-xs font-medium text-green-700 max-w-32">
                                    📞 <span className="truncate">{item.phone}</span>
                                  </div>
                                )}
                                
                                {/* Department */}
                                {item.department && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50 rounded-lg text-xs font-medium text-purple-700 max-w-32">
                                    🏢 <span className="truncate">{item.department}</span>
                                  </div>
                                )}
                                
                                {/* Source */}
                                {item.source && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 rounded-lg text-xs font-medium text-orange-700 max-w-28">
                                    📍 <span className="truncate">{item.source}</span>
                                  </div>
                                )}
                                
                                {/* Contact Type */}
                                {item.type && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded-lg text-xs font-medium text-gray-700 max-w-24">
                                    👤 <span className="truncate">{item.type}</span>
                                  </div>
                                )}
                                
                                {/* Account Value */}
                                {item.accountValue && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg text-xs font-medium text-green-700 max-w-28">
                                    💰 <span className="truncate">${item.accountValue}</span>
                                  </div>
                                )}
                                
                                {/* Last Contacted */}
                                {item.lastContacted && (
                                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-lg text-xs font-medium text-yellow-700 max-w-32">
                                    🕐 <span className="truncate">{new Date(item.lastContacted).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                          {/* For Teams */}
                          {entityType === 'teams' && (
                            <>
                              {/* Team Members - Prioritized Display */}
                              {(item.metadata.member_names || item.member_names) && (
                                <div className="w-full mb-3">
                                  <div className="text-xs font-medium text-gray-700 mb-2">Team Members:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {(() => {
                                      // Parse member names - handle both array and string formats
                                      let memberNames = item.metadata.member_names || item.member_names;
                                      let memberEmails = item.metadata.member_emails || item.member_emails;
                                      
                                      // Parse string arrays if needed
                                      if (typeof memberNames === 'string') {
                                        try {
                                          memberNames = memberNames.replace(/'/g, '"');
                                          memberNames = JSON.parse(memberNames);
                                        } catch {
                                          memberNames = memberNames.split(',').map(name => name.trim().replace(/[\[\]']/g, ''));
                                        }
                                      }
                                      
                                      if (typeof memberEmails === 'string') {
                                        try {
                                          memberEmails = memberEmails.replace(/'/g, '"');
                                          memberEmails = JSON.parse(memberEmails);
                                        } catch {
                                          memberEmails = memberEmails.split(',').map(email => email.trim().replace(/[\[\]']/g, ''));
                                        }
                                      }
                                      
                                      // Ensure arrays
                                      memberNames = Array.isArray(memberNames) ? memberNames : [];
                                      memberEmails = Array.isArray(memberEmails) ? memberEmails : [];
                                      
                                      return memberNames.map((name: string, idx: number) => (
                                        <div key={idx} className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                            <span className="text-white text-xs font-semibold">
                                              {name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </span>
                                          </div>
                                          <div className="text-left min-w-0 flex-1">
                                            <div className="text-sm font-medium text-gray-900 truncate">{name}</div>
                                            {memberEmails[idx] && (
                                              <div className="text-xs text-gray-600 truncate">{memberEmails[idx]}</div>
                                            )}
                                          </div>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </div>
                              )}
                            
                            {/* Team Metadata Row */}
                            <div className="flex flex-wrap gap-2">
                              {/* Team Icon */}
                              {item.metadata.icon && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-yellow-50 rounded-lg text-xs font-medium text-yellow-700 max-w-20">
                                  <span className="flex-shrink-0">{item.metadata.icon}</span>
                                  <span className="truncate">Team</span>
                                </div>
                              )}
                              
                              {/* Routing Strategy */}
                              {item.metadata.routingStrategy && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 rounded-lg text-xs font-medium text-indigo-700 max-w-32">
                                  <Settings className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{item.metadata.routingStrategy}</span>
                                </div>
                              )}
                              
                              {/* Ticket Limits */}
                              {item.metadata.maxOpenTickets && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 rounded-lg text-xs font-medium text-orange-700 max-w-32">
                                  <AlertCircle className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">Max {item.metadata.maxOpenTickets} tickets</span>
                                </div>
                              )}
                              
                              {/* Chat Limits */}
                              {item.metadata.maxActiveChats && (
                                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg text-xs font-medium text-green-700 max-w-28">
                                  <User className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">Max {item.metadata.maxActiveChats} chats</span>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Additional metadata can be shown here if needed */}
                      {item.metadata && Object.keys(item.metadata).length > 0 && (
                          <div className="flex gap-1 flex-wrap">{/* Keep for any additional metadata */}
                            
                            {/* Show other metadata */}
                            {Object.entries(item.metadata).slice(0, 3).map(([key, value]) => {
                              // Skip fields that are already displayed above or internal fields
                              if (key.includes('_raw') || key.includes('_id') || key.includes('_color') || 
                                  key.includes('created_at') || key.includes('updated_at') || 
                                  key === 'assignedTo' || key === 'assignedToName' ||
                                  key === 'customerId' || key === 'customerName' || key === 'customerEmail' ||
                                  key === 'teamNames' || key === 'channel' || key === 'last_message' ||
                                  key === 'unread' || key === 'status' || key === 'priority') {
                                return null;
                              }
                              
                              // Get color for priority and status
                              const colorKey = key === 'priority' ? 'priority_color' : key === 'status' ? 'status_color' : null;
                              const color = colorKey ? item.metadata[colorKey] : null;
                              
                              // Get emoji for priority
                              let displayValue = String(value);
                              if (key === 'priority' && structured_data?.controls?.priority_dropdown?.options) {
                                const priorityOption = structured_data.controls.priority_dropdown.options.find(
                                  (opt: any) => opt.value === item.metadata.priority_id || opt.name === value
                                );
                                if (priorityOption && priorityOption.emoji) {
                                  displayValue = `${priorityOption.emoji} ${value}`;
                                }
                              }
                              
                              return (
                                <Badge 
                                  key={key} 
                                  variant="outline" 
                                  className={`text-xs ${color ? '' : 'bg-gray-50 text-gray-600'}`}
                                  style={color ? {
                                    backgroundColor: `${color}20`,
                                    color: color,
                                    borderColor: `${color}60`
                                  } : {}}
                                >
                                  {key}: {displayValue}
                                </Badge>
                              );
                            })}
                          </div>
                        )}
                      
                      {/* Action Buttons - Removed per user request */}
                      
                      {/* Dropdown Controls */}
                      {renderDropdownControls(item)}
                    </div>
                    
                    <div className="flex-shrink-0 self-start mt-1">
                      <div className="text-xs text-gray-400">
                        {item.timestamp || <Clock className="h-3 w-3" />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Show More/Less Button */}
        {filteredItems.length > 10 && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="gap-2"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show {filteredItems.length - 10} More
                </>
              )}
            </Button>
          </div>
        )}

        {/* Global Action Links - Removed per user request */}

        {/* Footer Stats */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex justify-between text-xs text-gray-600">
            <span>
              Showing {displayItems.length} of {filteredItems.length} {entityType}
            </span>
            <span>
              Total: {totalCount}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};